import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import CloseIcon from "@mui/icons-material/Close";

const PIPELINE_TYPE_OPTIONS = [
  { label: "Personal", value: "personal" },
  { label: "Shared", value: "shared" },
];

export const EMPTY_FILTERS = {
  pipeline_type: [],
  owner_id: [],
  include_archived: false,
  assigned_to_me: true,
  has_urgent: false,
};

const chipSx = (active) => ({
  fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
  fontSize: 13,
  borderRadius: "0.6rem",
  height: 34,
  px: 0.5,
  cursor: "pointer",
  transition: "opacity 0.15s ease",
  ...(active
    ? {
      backgroundColor: "hsla(150, 38%, 17%, 0.08)",
      color: "hsl(150 38% 17%)",
      borderColor: "hsl(150 38% 17%)",
      "&:hover": { opacity: 0.8 },
    }
    : {
      backgroundColor: "#fff",
      color: "hsl(150 30% 12%)",
      borderColor: "hsl(140 8% 88%)",
      "&:hover": {
        borderColor: "hsl(140 8% 82%)",
      },
    }),
});

const FilterChip = ({ def, selected, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const count = selected.length;

  const handleToggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter((v) => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const options = def.options;

  return (
    <>
      <Chip
        variant="outlined"
        label={count > 0 ? `${def.label} (${count})` : def.label}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={chipSx(count > 0)}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              borderRadius: "0.75rem",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              minWidth: 220,
              maxHeight: 360,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 0.5 }}>
          {options.map((opt) => (
            <FormControlLabel
              key={opt.value}
              sx={{ display: "flex", mx: 0, px: 2, py: 0.25 }}
              control={
                <Checkbox
                  size="small"
                  disableRipple
                  checked={selected.includes(opt.value)}
                  onChange={() => handleToggle(opt.value)}
                  sx={{
                    color: "hsl(140 8% 82%)",
                    "&.Mui-checked": { color: "hsl(150 38% 17%)" },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: 13, color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Regular, sans-serif, system-ui" }}>
                  {opt.label}
                </Typography>
              }
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

const PipelineTableFilters = ({ filters, onFiltersChange, owners = [], hasExpiringOffer = false }) => {
  const filterDefs = useMemo(
    () => {
      const defs = [
        {
          id: "pipeline_type",
          label: "Type",
          options: PIPELINE_TYPE_OPTIONS,
        },
      ];

      if (owners.length > 0) {
        defs.push({
          id: "owner_id",
          label: "Owner",
          options: owners.map((u) => ({ label: u.full_name || u.email, value: String(u.id) })),
        });
      }

      return defs;
    },
    [owners],
  );

  const totalActive = useMemo(
    () => Object.entries(filters).reduce((sum, [key, val]) => {
      if (key === "assigned_to_me") return sum;
      if (key === "include_archived" || key === "has_urgent") return sum + (val ? 1 : 0);
      return sum + (Array.isArray(val) ? val.length : 0);
    }, 0),
    [filters],
  );

  const handleChange = useCallback(
    (filterId) => (newValues) => {
      onFiltersChange({ ...filters, [filterId]: newValues });
    },
    [filters, onFiltersChange],
  );

  const handleClearAll = useCallback(() => {
    onFiltersChange({ ...EMPTY_FILTERS });
  }, [onFiltersChange]);

  return (
    <Box
      sx={{
        px: 3,
        py: 1,
        backgroundColor: "#fff",
        borderBottom: "1px solid hsl(140 8% 88%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <FilterListIcon sx={{ fontSize: 18, color: "hsl(150 30% 12%)", opacity: 0.5, mr: 0.5 }} />

        {filterDefs.map((def) => (
          <FilterChip
            key={def.id}
            def={def}
            selected={filters[def.id] || []}
            onChange={handleChange(def.id)}
          />
        ))}

        <Chip
          variant="outlined"
          label="Assigned to me"
          onClick={() => onFiltersChange({ ...filters, assigned_to_me: !filters.assigned_to_me })}
          sx={chipSx(filters.assigned_to_me)}
        />

        <Chip
          variant="outlined"
          label="Urgent"
          onClick={() => onFiltersChange({ ...filters, has_urgent: !filters.has_urgent })}
          sx={chipSx(filters.has_urgent)}
        />

        <Chip
          variant="outlined"
          label="Archived"
          onClick={() => onFiltersChange({ ...filters, include_archived: !filters.include_archived })}
          sx={chipSx(filters.include_archived)}
        />

        {totalActive > 0 && (
          <Chip
            size="small"
            label="Clear all"
            deleteIcon={<CloseIcon sx={{ fontSize: "14px !important", color: "hsl(0 72% 51%) !important" }} />}
            onDelete={handleClearAll}
            onClick={handleClearAll}
            sx={{
              ml: 0.5,
              height: 28,
              fontFamily: "Segoe UI Semibold, sans-serif, system-ui",
              fontSize: 12,
              color: "hsl(0 72% 51%)",
              borderColor: "hsl(0 72% 51%)",
              backgroundColor: "rgba(211,47,47,0.06)",
              "&:hover": { backgroundColor: "rgba(211,47,47,0.12)" },
            }}
            variant="outlined"
          />
        )}

        {hasExpiringOffer && (
          <Typography
            sx={{
              ml: "auto",
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              fontSize: 12,
              fontFamily: "Segoe UI Regular, sans-serif, system-ui",
              color: "hsl(150 10% 46%)",
              whiteSpace: "nowrap",
            }}
          >
            <Box component="span" sx={{ color: "#D32F2F", fontWeight: 700, fontSize: 14, lineHeight: 1 }}>
              *
            </Box>
            Rows in red have a job offer expiring within 7 days
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

export default PipelineTableFilters;
